import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

import { getCurrentCardAssociatedWithTheUserAccount } from '@src/api'
import { GetCurrentCardAssociatedWithTheUserAccount200Response } from '@src/api/generated/user-api'
import { REACT_QUERY_CACHE_KEYS } from '@src/consts'
import { useAxios } from '@src/hooks'
import { CustomError } from '@src/types'

type Options = {
  /**
   * Whether to include available merchant categories in the response.
   * Set to false to improve performance when merchant categories are not needed.
   *
   * Note: Ideally default should be false and callers who need it should pass true.
   * However, since existing behavior was to always return merchant categories,
   * we keep the default as true for backward compatibility to minimize impact.
   * @default true
   */
  includeMerchantCategories?: boolean
}

export const useFetchCurrentCardAssociatedWithTheUserAccount = (
  userAccountId: string,
  options: Options = {}
) => {
  const { includeMerchantCategories = true } = options
  const { axiosInstance } = useAxios()
  return useQuery<
    AxiosResponse<GetCurrentCardAssociatedWithTheUserAccount200Response>,
    AxiosResponse<CustomError>
  >({
    enabled: !!userAccountId,
    queryFn: () =>
      getCurrentCardAssociatedWithTheUserAccount(axiosInstance, userAccountId, {
        includeMerchantCategories,
      }),
    queryKey: [
      REACT_QUERY_CACHE_KEYS.GET_CURRENT_CARD_ASSOCIATED_WITH_THE_USER_ACCOUNT,
      userAccountId,
      includeMerchantCategories,
    ],
  })
}
